import { AxiosResponse } from "axios";
import moment from "moment";
import { memo, useCallback, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import RequisitionType from "../../../Services/Requisition/RequisitionTypeService";
import DatePicker from "../../../Shared/Common/DatePickerResultData/DatePicker";
import LoadButton from "../../../Shared/Common/LoadButton";
import PermissionComponent, {
  getPermissionDisplayName,
} from "../../../Shared/Common/Permissions/PermissionComponent";
import TimePicker from "../../../Shared/Common/TimePickerResultData/TimePicker";
import { useResultDataContext } from "../../../Shared/OncologyResultDataContext";
import { reactSelectSMStyle, styles } from "../../../Utils/Common";
import { upsertArray } from "../../../Utils/Common/Requisition";
import useLang from "Shared/hooks/useLanguage";
import SearchDatePicker from "Shared/Common/DatePicker/SearchDatePicker";

const RenderInput = ({
  tabsDetail,
  currentColumnKey,
  setCurrentColumnKey,
  tabValue,
  setTriggerSearchData,
  setCurPage,
}: any) => {
  const {
    data,
    filterData,
    setFilterData,
    searchValue,
    setSearchValue,
    selectedBox,
    apiCalls,
  } = useResultDataContext();

  const { t } = useLang();
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ---------------------------------- Utils --------------------------------- */

  const updateFilters = useCallback(
    (value: string) => {
      const filterObj = {
        columnName: tabsDetail.filterColumns,
        filterValue: value,
        columnType: tabsDetail.filterColumnsType,
        label: tabsDetail.columnLabel,
        columnKey: tabsDetail.columnKey,
      };

      const updatedFilters = upsertArray(
        filterData.filters,
        filterObj,
        (el: any) => el.columnName === filterObj.columnName
      ).filter((el: any) => el.filterValue !== "");

      setFilterData({ ...filterData, filters: updatedFilters });
    },
    [filterData, setFilterData, tabsDetail]
  );

  const handleKeyPress = (e: any) => {
    if (e.key === "Enter") {
      setCurPage(1);
      setTriggerSearchData((prev: any) => !prev);
    }
  };

  /* ------------------------------ Input Change ------------------------------- */

  const handleChange = (e: any) => {
    let value = e.target.value;

    if (e.target.type === "date") {
      value = moment(value, "YYYY-MM-DD").format("MM/DD/YYYY");
    }

    setSearchValue((prev: any) => ({
      ...prev,
      [tabsDetail.columnKey]: value,
    }));

    updateFilters(value);
  };

  const handleSelectChange = (e: any) => {
    const value = String(e.value);

    setSearchValue((prev: any) => ({
      ...prev,
      [tabsDetail.columnKey]: value,
    }));

    updateFilters(value);
  };

  const getDropdownOptions = () => {
    if (tabsDetail.columnKey?.toLowerCase() === "requisitionstatus") {
      return data?.requisitionStatusLookup ?? [];
    }

    if (!tabsDetail?.jsonOptionData) return [];

    try {
      const parsed = JSON.parse(tabsDetail.jsonOptionData);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  /* --------------------------- Bulk Publish Logic ----------------------------- */

  const BulkPublishAndValidate = () => {
    setIsSubmitting(true);

    if (!selectedBox.requisitionOrderId.length) {
      toast.error("Please select atleast one record");
      setIsSubmitting(false);
      return;
    }

    const pendingList: any[] = [];

    selectedBox.requisitionOrderId.forEach((record: any) => {
      const foundRecord = data?.gridData?.data?.data?.find(
        (item: any) => item.requisitionOrderId === record.requisitionOrderId
      );

      if (
        foundRecord?.lisStatus?.toLowerCase() === "pending" ||
        foundRecord?.lisStatus?.toLowerCase() === "ready for api" ||
        foundRecord?.lisStatus?.toLowerCase() === "Sent to api"
      ) {
        pendingList.push(foundRecord);
      }
    });

    if (pendingList.length > 0) {
      toast.error("Please publish the order first.");
      setIsSubmitting(false);
      return;
    }

    RequisitionType.BulkPublishAndValidate(selectedBox.requisitionOrderId)
      .then((res: AxiosResponse) => {
        if (res?.status === 200) {
          toast.success(res?.data?.message);
          apiCalls();
        }
      })
      .catch((err: any) => {
        console.error(err);
      })
      .finally(() => setIsSubmitting(false));
  };

  /* ------------------------------- Dropdowns -------------------------------- */

  const getValueLabelPair = (list: any[], key: string, searchObj: any) => {
    const selectedValue = searchObj[key];
    if (
      selectedValue === undefined ||
      selectedValue === null ||
      selectedValue === ""
    ) {
      return null;
    }

    const match = list?.find(
      (item) => String(item.value) === String(selectedValue)
    );

    return match ? { value: match.value, label: match.label } : null;
  };

  /* ---------------------------------- JSX ----------------------------------- */

  return (
    <div>
      {tabsDetail.filterColumnsType === "text" && (
        <input
          id={`IDResultDataSearch_${tabsDetail.columnKey}`}
          type="text"
          autoComplete="off"
          className="form-control bg-white mb-lg-0 h-30px rounded-2 fs-8 w-100"
          placeholder={t("Search ...")}
          value={searchValue[tabsDetail.columnKey]}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
        />
      )}

      {tabsDetail.filterColumnsType === "dateRange" && (
        <DatePicker
          tabsDetail={tabsDetail}
          columnKey={tabsDetail.columnKey}
          currentColumnKey={currentColumnKey}
          setCurrentColumnKey={setCurrentColumnKey}
          lisType="ID"
        />
      )}

      {tabsDetail.filterColumnsType === "DatePicker" && (
        <SearchDatePicker
          value={searchValue[tabsDetail.columnKey]}
          onChange={handleChange}
        />
      )}

      {tabsDetail.filterColumnsType === "timeRange" && (
        <TimePicker
          columnKey={tabsDetail.filterColumns}
          tabsDetail={tabsDetail}
        />
      )}

      {tabsDetail.filterColumnsType === "dropdown" && (
        <Select
          inputId={`IDResultDataSearch_${tabsDetail.columnKey}`}
          menuPortalTarget={document.body}
          theme={(theme) => styles(theme)}
          placeholder={t("Select...")}
          onChange={handleSelectChange}
          isSearchable
          styles={reactSelectSMStyle}
          onKeyDown={handleKeyPress}
          options={getDropdownOptions()}
          value={getValueLabelPair(
            getDropdownOptions(),
            tabsDetail.columnKey,
            searchValue
          )}
        />
      )}

      {tabValue === 0 && tabsDetail.filterColumnsType === "button" && (
        <PermissionComponent
          moduleName="Oncology LIS"
          pageName="Result Data"
          permissionIdentifier="BulkPublishandValidate"
        >
          {({ pageName, moduleName, permissionIdentifier }) => (
            <LoadButton
              name={`OncologyResultDataSearch_${tabsDetail?.columnKey}`}
              className="btn btn-sm fw-bold btn-twitter rounded-3"
              // disabled={item.azureLink == null ? true : false}
              onClick={() => BulkPublishAndValidate()}
              loading={isSubmitting}
              btnText={getPermissionDisplayName(
                pageName,
                moduleName,
                permissionIdentifier,
                "Bulk Publish & Validate"
              )}
              loadingText={t("Processing...")}
            />
          )}
        </PermissionComponent>
      )}
    </div>
  );
};

export default memo(RenderInput);
